import $ from 'jquery';


export default () => {

    const nodeSelectors = {

        container:
            '[js-big-video="container"]',

        video:
            '[js-big-video="video"]',

        soundBtn:
            '[js-big-video="sound"]',

        playBtn:
            '[js-big-video="play"]',

        /**
         * IMPORTANT:
         *
         * This is the OUTER slider wrapper.
         *
         * Do NOT use this to determine if a video
         * belongs to media-slider.js because your
         * desktop main-product-video also lives
         * inside this wrapper.
         */
        sliderContainer:
            '[js-media-slider="container"]',

        /**
         * ACTUAL Swiper slider.
         *
         * Only videos inside this element are
         * controlled by media-slider.js.
         */
        slider:
            '[js-media-slider="slider"]',

        mediaSnippet:
            '.media-snippet',

        videoPoster:
            '[data-video-poster]',

        fancybox:
            '[data-fancybox]',

    };


    /**
     * Must match your CSS/video breakpoint.
     */
    const desktopMediaQuery =
        window.matchMedia(
            '(min-width: 768px)'
        );


    let autoplayObserver =
        null;


    let mutationObserver =
        null;



    /**
     * ============================================================
     * INIT
     * ============================================================
     */

    function init() {

        /**
         * Global delegated play/sound handlers.
         *
         * Delegation means controls continue working
         * if Shopify updates/re-renders product markup.
         */
        bindDelegatedControls();


        /**
         * Initialize existing standalone videos.
         */
        initSections();


        /**
         * Desktop/mobile source switching.
         */
        bindBreakpointListener();


        /**
         * Handle videos injected later.
         */
        bindMutationObserver();

    }



    /**
     * ============================================================
     * VIDEO OWNERSHIP
     * ============================================================
     *
     * media.js:
     * standalone videos
     *
     * media-slider.js:
     * only videos actually inside
     * [js-media-slider="slider"]
     *
     * Fancybox:
     * gallery videos
     */

    function isVideoControlledElsewhere(
        video
    ) {

        if (!video) {
            return true;
        }


        /**
         * IMPORTANT FIX
         *
         * Check ACTUAL slider.
         *
         * We previously checked:
         *
         * [js-media-slider="container"]
         *
         * which caused desktop main-product-video
         * to be incorrectly ignored.
         */
        if (
            video.closest(
                nodeSelectors.slider
            )
        ) {

            return true;

        }


        /**
         * Fancybox gallery owns this video.
         */
        if (
            video.closest(
                nodeSelectors.fancybox
            )
        ) {

            return true;

        }


        return false;

    }



    /**
     * ============================================================
     * GET RESPONSIVE SHOPIFY VIDEO SOURCE
     * ============================================================
     */

    function getResponsiveVideoSource(
        video
    ) {

        if (!video) {
            return null;
        }


        const desktopSource =
            video.getAttribute(
                'data-desktop-src'
            );


        const mobileSource =
            video.getAttribute(
                'data-mobile-src'
            );


        /**
         * New deferred media.liquid structure.
         */
        if (
            desktopSource ||
            mobileSource
        ) {

            /**
             * Desktop.
             *
             * Desktop source first.
             * Mobile fallback.
             */
            if (
                desktopMediaQuery.matches
            ) {

                return (
                    desktopSource ||
                    mobileSource ||
                    null
                );

            }


            /**
             * Mobile.
             *
             * Mobile source first.
             * Desktop fallback.
             */
            return (
                mobileSource ||
                desktopSource ||
                null
            );

        }


        return null;

    }



    /**
     * ============================================================
     * CHECK TRADITIONAL SOURCES
     * ============================================================
     */

    function hasTraditionalSource(
        video
    ) {

        if (!video) {
            return false;
        }


        /**
         * Direct:
         *
         * <video src="...">
         */
        const directSrc =
            video.getAttribute(
                'src'
            );


        if (
            directSrc &&
            directSrc !== 'about:blank'
        ) {

            return true;

        }


        /**
         * Traditional:
         *
         * <video>
         *     <source src="...">
         * </video>
         */
        const sources =
            Array.from(
                video.querySelectorAll(
                    'source'
                )
            );


        if (!sources.length) {
            return false;
        }


        return sources.some(
            (source) => {

                if (!source) {
                    return false;
                }


                return Boolean(
                    source.getAttribute(
                        'src'
                    ) ||
                    source.getAttribute(
                        'data-src'
                    )
                );

            }
        );

    }



    /**
     * ============================================================
     * LOAD VIDEO
     * ============================================================
     *
     * Supports:
     *
     * NEW:
     *
     * data-desktop-src
     * data-mobile-src
     *
     * OLD:
     *
     * <source data-src="">
     *
     * NORMAL:
     *
     * <source src="">
     */

    function loadLazyVideo(
        video,
        {
            force = false
        } = {}
    ) {

        if (!video) {
            return false;
        }



        /**
         * ========================================================
         * NEW DEFERRED STRUCTURE
         * ========================================================
         */

        const responsiveSource =
            getResponsiveVideoSource(
                video
            );


        if (responsiveSource) {

            /**
             * Already using correct source.
             */
            if (
                !force &&
                video.dataset.loadedSource ===
                    responsiveSource &&
                video.getAttribute('src') ===
                    responsiveSource
            ) {

                return true;

            }


            video.dataset.loaded =
                'true';


            video.dataset.loadedSource =
                responsiveSource;


            video.preload =
                'auto';


            /**
             * Explicitly assign responsive source.
             */
            video.src =
                responsiveSource;


            /**
             * Start browser loading.
             */
            video.load();


            return true;

        }



        /**
         * ========================================================
         * LEGACY source[data-src]
         * ========================================================
         */

        const lazySources =
            Array.from(
                video.querySelectorAll(
                    'source[data-src]'
                )
            );


        if (
            lazySources.length
        ) {

            lazySources.forEach(
                (sourceElement) => {

                    if (!sourceElement) {
                        return;
                    }


                    const sourceUrl =
                        sourceElement.getAttribute(
                            'data-src'
                        );


                    if (!sourceUrl) {
                        return;
                    }


                    sourceElement.setAttribute(
                        'src',
                        sourceUrl
                    );


                    sourceElement.removeAttribute(
                        'data-src'
                    );

                }
            );


            video.preload =
                'auto';


            video.load();


            video.dataset.loaded =
                'true';


            return true;

        }



        /**
         * ========================================================
         * NORMAL EXISTING VIDEO
         * ========================================================
         */

        if (
            hasTraditionalSource(
                video
            )
        ) {

            video.dataset.loaded =
                'true';


            return true;

        }



        console.warn(
            'media.js: No usable source found for video:',
            video
        );


        return false;

    }



    /**
     * ============================================================
     * MANUAL PLAY
     * ============================================================
     */

    async function playVideo(
        video,
        playVideoBtn = null,
        {
            showControls = true,
            unmute = true
        } = {}
    ) {

        if (!video) {
            return false;
        }


        /**
         * Assign responsive source immediately.
         */
        const loaded =
            loadLazyVideo(
                video
            );


        if (!loaded) {

            console.error(
                'media.js: Could not load video:',
                video
            );


            return false;

        }


        video.playsInline =
            true;



        /**
         * Manual click = user interaction.
         *
         * Therefore we can unmute.
         */
        if (
            unmute
        ) {

            video.muted =
                false;

        }



        if (
            showControls
        ) {

            video.setAttribute(
                'controls',
                ''
            );

        }



        try {

            /**
             * IMPORTANT:
             *
             * Call play() immediately after assigning src.
             *
             * Browser handles waiting for enough video data.
             */
            const playPromise =
                video.play();


            if (
                playPromise &&
                typeof playPromise.then ===
                    'function'
            ) {

                await playPromise;

            }



            if (
                playVideoBtn
            ) {

                playVideoBtn
                    .classList
                    .remove(
                        'paused'
                    );


                $(playVideoBtn)
                    .stop(
                        true,
                        true
                    )
                    .fadeOut();

            }


            return true;

        } catch (error) {

            console.error(
                'media.js: MANUAL VIDEO PLAY FAILED',
                {
                    error,
                    video,

                    currentSrc:
                        video.currentSrc,

                    src:
                        video.getAttribute(
                            'src'
                        ),

                    desktopSource:
                        video.getAttribute(
                            'data-desktop-src'
                        ),

                    mobileSource:
                        video.getAttribute(
                            'data-mobile-src'
                        ),

                    readyState:
                        video.readyState,

                    networkState:
                        video.networkState,

                    paused:
                        video.paused,

                    muted:
                        video.muted,
                }
            );


            handlePlayError(
                error
            );


            return false;

        }

    }



    /**
     * ============================================================
     * AUTOPLAY
     * ============================================================
     */

    async function autoplayVideo(
        video
    ) {

        if (!video) {
            return;
        }


        const loaded =
            loadLazyVideo(
                video
            );


        if (!loaded) {
            return;
        }


        /**
         * Required by autoplay policies.
         */
        video.muted =
            true;


        video.defaultMuted =
            true;


        video.playsInline =
            true;



        try {

            const playPromise =
                video.play();


            if (
                playPromise &&
                typeof playPromise.then ===
                    'function'
            ) {

                await playPromise;

            }

        } catch (error) {

            handlePlayError(
                error
            );

        }

    }



    /**
     * ============================================================
     * PAUSE
     * ============================================================
     */

    function pauseVideo(
        video,
        playVideoBtn = null
    ) {

        if (!video) {
            return;
        }


        video.pause();


        video.removeAttribute(
            'controls'
        );


        if (
            playVideoBtn
        ) {

            playVideoBtn
                .classList
                .add(
                    'paused'
                );


            $(playVideoBtn)
                .stop(
                    true,
                    true
                )
                .fadeIn();

        }

    }



    /**
     * ============================================================
     * ERRORS
     * ============================================================
     */

    function handlePlayError(
        error
    ) {

        if (!error) {
            return;
        }



        /**
         * Common if another load interrupts
         * a previous play call.
         */
        if (
            error.name ===
            'AbortError'
        ) {

            return;

        }



        if (
            error.name ===
            'NotAllowedError'
        ) {

            console.warn(
                'media.js: Browser prevented playback:',
                error
            );


            return;

        }



        console.warn(
            'media.js: Playback failed:',
            error
        );

    }



    /**
     * ============================================================
     * FIND VIDEO BELONGING TO BUTTON
     * ============================================================
     */

    function findVideoForControl(
        control
    ) {

        if (!control) {
            return null;
        }



        /**
         * BEST MATCH:
         *
         * media.liquid puts button and video inside
         * the same .media-snippet.
         */
        const mediaSnippet =
            control.closest(
                nodeSelectors.mediaSnippet
            );


        if (
            mediaSnippet
        ) {

            const video =
                mediaSnippet.querySelector(
                    nodeSelectors.video
                );


            if (
                video &&
                !isVideoControlledElsewhere(
                    video
                )
            ) {

                return video;

            }

        }



        /**
         * FALLBACK:
         *
         * closest js-big-video container.
         */
        const videoContainer =
            control.closest(
                nodeSelectors.container
            );


        if (!videoContainer) {

            console.warn(
                'media.js: Control has no js-big-video container:',
                control
            );


            return null;

        }



        const videos =
            Array.from(
                videoContainer.querySelectorAll(
                    nodeSelectors.video
                )
            )
            .filter(
                (video) => {

                    return !isVideoControlledElsewhere(
                        video
                    );

                }
            );



        if (
            videos.length === 1
        ) {

            return videos[0];

        }



        if (
            videos.length > 1
        ) {

            console.warn(
                'media.js: Multiple videos found for one control:',
                {
                    control,
                    videos,
                    container:
                        videoContainer
                }
            );

        }



        return null;

    }



    /**
     * ============================================================
     * DELEGATED CONTROLS
     * ============================================================
     *
     * Capture mode is intentional.
     */

    function bindDelegatedControls() {

        if (
            document.documentElement.dataset
                .mediaControlsBound ===
            'true'
        ) {

            return;

        }



        document.documentElement.dataset
            .mediaControlsBound =
            'true';



        document.addEventListener(
            'click',
            async (event) => {

                const target =
                    event.target instanceof Element
                        ? event.target
                        : null;


                if (!target) {
                    return;
                }



                /**
                 * ==================================================
                 * PLAY BUTTON
                 * ==================================================
                 */

                const playButton =
                    target.closest(
                        nodeSelectors.playBtn
                    );


                if (
                    playButton
                ) {



                    /**
                     * IMPORTANT:
                     *
                     * Only ignore play buttons actually
                     * inside the REAL slider.
                     *
                     * Do NOT use sliderContainer here.
                     */
                    if (
                        playButton.closest(
                            nodeSelectors.slider
                        )
                    ) {

                        return;

                    }



                    /**
                     * Fancybox owns gallery buttons.
                     */
                    if (
                        playButton.closest(
                            nodeSelectors.fancybox
                        )
                    ) {

                        return;

                    }



                    const video =
                        findVideoForControl(
                            playButton
                        );


                    if (!video) {

                        console.error(
                            'media.js: PLAY BUTTON HAS NO MATCHING VIDEO:',
                            playButton
                        );


                        return;

                    }



                    /**
                     * Prepare dynamically-added video if necessary.
                     */
                    initializeVideo(
                        video
                    );



                    event.preventDefault();



                    console.log(
                        'media.js: PLAY CLICK',
                        {
                            button:
                                playButton,

                            video,

                            desktop:
                                desktopMediaQuery.matches,

                            desktopSource:
                                video.getAttribute(
                                    'data-desktop-src'
                                ),

                            mobileSource:
                                video.getAttribute(
                                    'data-mobile-src'
                                ),

                            currentSrc:
                                video.currentSrc,
                        }
                    );



                    if (
                        video.paused
                    ) {

                        await playVideo(
                            video,
                            playButton,
                            {
                                showControls:
                                    true,

                                unmute:
                                    true
                            }
                        );

                    } else {

                        pauseVideo(
                            video,
                            playButton
                        );

                    }



                    return;

                }



                /**
                 * ==================================================
                 * SOUND BUTTON
                 * ==================================================
                 */

                const soundButton =
                    target.closest(
                        nodeSelectors.soundBtn
                    );


                if (!soundButton) {
                    return;
                }



                /**
                 * Actual slider owns its sound controls.
                 */
                if (
                    soundButton.closest(
                        nodeSelectors.slider
                    )
                ) {

                    return;

                }



                if (
                    soundButton.closest(
                        nodeSelectors.fancybox
                    )
                ) {

                    return;

                }



                const video =
                    findVideoForControl(
                        soundButton
                    );


                if (!video) {

                    console.warn(
                        'media.js: Sound button has no matching video:',
                        soundButton
                    );


                    return;

                }



                initializeVideo(
                    video
                );



                event.preventDefault();



                const loaded =
                    loadLazyVideo(
                        video
                    );


                if (!loaded) {
                    return;
                }



                if (
                    video.muted
                ) {

                    video.muted =
                        false;


                    soundButton
                        .classList
                        .add(
                            'unmuted'
                        );

                } else {

                    video.muted =
                        true;


                    soundButton
                        .classList
                        .remove(
                            'unmuted'
                        );

                }

            },

            /**
             * Capture mode.
             *
             * We receive the click before product-page
             * scripts can stopPropagation().
             */
            true
        );

    }



    /**
     * ============================================================
     * VIDEO EVENTS
     * ============================================================
     */

    function prepareVideoEvents(
        video
    ) {

        if (!video) {
            return;
        }



        if (
            video.dataset.mediaEventsPrepared ===
            'true'
        ) {

            return;

        }



        video.dataset.mediaEventsPrepared =
            'true';



        const mediaSnippet =
            video.closest(
                nodeSelectors.mediaSnippet
            );



        /**
         * Actual video playback started.
         */
        video.addEventListener(
            'playing',
            () => {

                if (!mediaSnippet) {
                    return;
                }


                mediaSnippet
                    .classList
                    .add(
                        'is-video-playing'
                    );

            }
        );



        /**
         * Manual video paused.
         */
        video.addEventListener(
            'pause',
            () => {

                const isAutoplay =
                    (
                        video.getAttribute(
                            'data-video-autoplay'
                        ) === 'true' ||

                        video.classList.contains(
                            'autoplay_video'
                        )
                    );


                if (
                    isAutoplay
                ) {

                    return;

                }



                if (!mediaSnippet) {
                    return;
                }



                const playButton =
                    mediaSnippet.querySelector(
                        nodeSelectors.playBtn
                    );


                if (!playButton) {
                    return;
                }



                video.removeAttribute(
                    'controls'
                );


                playButton
                    .classList
                    .add(
                        'paused'
                    );


                $(playButton)
                    .stop(
                        true,
                        true
                    )
                    .fadeIn();

            }
        );



        /**
         * Actual video element error.
         */
        video.addEventListener(
            'error',
            () => {

                console.error(
                    'media.js: VIDEO ERROR',
                    {
                        video,

                        currentSrc:
                            video.currentSrc,

                        src:
                            video.getAttribute(
                                'src'
                            ),

                        desktopSource:
                            video.getAttribute(
                                'data-desktop-src'
                            ),

                        mobileSource:
                            video.getAttribute(
                                'data-mobile-src'
                            ),

                        readyState:
                            video.readyState,

                        networkState:
                            video.networkState,

                        error:
                            video.error,
                    }
                );

            }
        );

    }



    /**
     * ============================================================
     * SHOW POSTER
     * ============================================================
     */

    function showVideoPoster(
        video
    ) {

        if (!video) {
            return;
        }



        const mediaSnippet =
            video.closest(
                nodeSelectors.mediaSnippet
            );


        if (!mediaSnippet) {
            return;
        }



        mediaSnippet
            .classList
            .remove(
                'is-video-playing'
            );

    }



    /**
     * ============================================================
     * AUTOPLAY OBSERVER
     * ============================================================
     */

    function getAutoplayObserver() {

        if (
            autoplayObserver
        ) {

            return autoplayObserver;

        }



        if (
            !(
                'IntersectionObserver'
                in window
            )
        ) {

            return null;

        }



        autoplayObserver =
            new IntersectionObserver(
                (entries) => {

                    entries.forEach(
                        (entry) => {

                            const video =
                                entry.target;


                            if (!video) {
                                return;
                            }



                            if (
                                entry.isIntersecting
                            ) {

                                autoplayVideo(
                                    video
                                );


                                return;

                            }



                            if (
                                !video.paused
                            ) {

                                video.pause();

                            }

                        }
                    );

                },
                {

                    rootMargin:
                        '300px 0px',

                    threshold:
                        0.01,

                }
            );



        return autoplayObserver;

    }



    function initAutoplayObserver(
        video
    ) {

        if (!video) {
            return;
        }



        const shouldAutoplay =
            (
                video.getAttribute(
                    'data-video-autoplay'
                ) === 'true' ||

                video.classList.contains(
                    'autoplay_video'
                )
            );



        if (
            !shouldAutoplay
        ) {

            return;

        }



        /**
         * Only deferred autoplay needs observer.
         */
        if (
            !video.hasAttribute(
                'data-deferred-video'
            )
        ) {

            return;

        }



        if (
            video.dataset.autoplayObserved ===
            'true'
        ) {

            return;

        }



        video.dataset.autoplayObserved =
            'true';



        const observer =
            getAutoplayObserver();



        if (!observer) {

            autoplayVideo(
                video
            );


            return;

        }



        observer.observe(
            video
        );

    }



    /**
     * ============================================================
     * INITIALIZE ONE VIDEO
     * ============================================================
     */

    function initializeVideo(
        video
    ) {

        if (!video) {
            return;
        }



        if (
            isVideoControlledElsewhere(
                video
            )
        ) {

            return;

        }



        video.dataset.mediaOwned =
            'true';



        prepareVideoEvents(
            video
        );



        initAutoplayObserver(
            video
        );



        video.dataset.mediaInitialized =
            'true';

    }



    /**
     * ============================================================
     * INITIALIZE PAGE VIDEOS
     * ============================================================
     */

    function initSections() {

        const videos =
            document.querySelectorAll(
                nodeSelectors.video
            );


        if (!videos.length) {
            return;
        }



        videos.forEach(
            (video) => {

                /**
                 * Standalone media.js video must belong to
                 * js-big-video container.
                 */
                const container =
                    video.closest(
                        nodeSelectors.container
                    );


                if (!container) {
                    return;
                }



                initializeVideo(
                    video
                );

            }
        );

    }



    /**
     * ============================================================
     * RESPONSIVE DESKTOP / MOBILE SWITCH
     * ============================================================
     */

    async function handleBreakpointChange(
        video
    ) {

        if (!video) {
            return;
        }



        if (
            isVideoControlledElsewhere(
                video
            )
        ) {

            return;

        }



        if (
            !video.hasAttribute(
                'data-desktop-src'
            ) &&
            !video.hasAttribute(
                'data-mobile-src'
            )
        ) {

            return;

        }



        const newSource =
            getResponsiveVideoSource(
                video
            );


        if (!newSource) {
            return;
        }



        /**
         * Video was never loaded.
         *
         * Keep it lazy.
         */
        if (
            video.dataset.loaded !==
            'true'
        ) {

            return;

        }



        /**
         * Already correct source.
         */
        if (
            video.dataset.loadedSource ===
            newSource
        ) {

            return;

        }



        const wasPlaying =
            !video.paused;


        const wasMuted =
            video.muted;


        const hadControls =
            video.hasAttribute(
                'controls'
            );


        const shouldAutoplay =
            (
                video.getAttribute(
                    'data-video-autoplay'
                ) === 'true' ||

                video.classList.contains(
                    'autoplay_video'
                )
            );



        /**
         * Stop old responsive file.
         */
        video.pause();



        /**
         * Show poster while changing source.
         */
        showVideoPoster(
            video
        );



        const loaded =
            loadLazyVideo(
                video,
                {
                    force: true
                }
            );


        if (!loaded) {
            return;
        }



        if (
            hadControls
        ) {

            video.setAttribute(
                'controls',
                ''
            );

        }



        if (
            shouldAutoplay
        ) {

            video.muted =
                true;


            video.defaultMuted =
                true;

        } else {

            video.muted =
                wasMuted;

        }



        if (
            wasPlaying ||
            shouldAutoplay
        ) {

            try {

                const playPromise =
                    video.play();


                if (
                    playPromise &&
                    typeof playPromise.then ===
                        'function'
                ) {

                    await playPromise;

                }

            } catch (error) {

                handlePlayError(
                    error
                );

            }

        }

    }



    /**
     * ============================================================
     * GLOBAL BREAKPOINT LISTENER
     * ============================================================
     */

    function bindBreakpointListener() {

        if (
            document.documentElement.dataset
                .mediaBreakpointBound ===
            'true'
        ) {

            return;

        }



        document.documentElement.dataset
            .mediaBreakpointBound =
            'true';



        const breakpointHandler =
            () => {

                const videos =
                    document.querySelectorAll(
                        nodeSelectors.video
                    );


                videos.forEach(
                    (video) => {

                        handleBreakpointChange(
                            video
                        );

                    }
                );

            };



        if (
            typeof desktopMediaQuery
                .addEventListener ===
            'function'
        ) {

            desktopMediaQuery
                .addEventListener(
                    'change',
                    breakpointHandler
                );

        } else {

            desktopMediaQuery
                .addListener(
                    breakpointHandler
                );

        }

    }



    /**
     * ============================================================
     * DYNAMIC SHOPIFY CONTENT
     * ============================================================
     */

    function bindMutationObserver() {

        if (
            mutationObserver
        ) {

            return;

        }



        if (
            !(
                'MutationObserver'
                in window
            )
        ) {

            return;

        }



        mutationObserver =
            new MutationObserver(
                (mutations) => {

                    mutations.forEach(
                        (mutation) => {

                            mutation.addedNodes.forEach(
                                (node) => {

                                    if (
                                        !(
                                            node instanceof Element
                                        )
                                    ) {

                                        return;

                                    }



                                    /**
                                     * Added node itself is a video.
                                     */
                                    if (
                                        node.matches(
                                            nodeSelectors.video
                                        )
                                    ) {

                                        const container =
                                            node.closest(
                                                nodeSelectors.container
                                            );


                                        if (
                                            container
                                        ) {

                                            initializeVideo(
                                                node
                                            );

                                        }

                                    }



                                    /**
                                     * Added subtree contains videos.
                                     */
                                    const videos =
                                        node.querySelectorAll(
                                            nodeSelectors.video
                                        );


                                    videos.forEach(
                                        (video) => {

                                            const container =
                                                video.closest(
                                                    nodeSelectors.container
                                                );


                                            if (!container) {
                                                return;
                                            }


                                            initializeVideo(
                                                video
                                            );

                                        }
                                    );

                                }
                            );

                        }
                    );

                }
            );



        mutationObserver.observe(
            document.body,
            {
                childList: true,
                subtree: true,
            }
        );

    }



    /**
     * ============================================================
     * PUBLIC
     * ============================================================
     */

    return Object.freeze({

        init,

        initSections,

        loadLazyVideo,

        playVideo,

    });

};